// Chemin: C:\gestion-copro\src\services\quota.service.ts
// Service de gestion des quotas SENTINELLE
// Session 79 : Création du système de garde-fous quota

import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { 
  FormuleAbonnement, 
  QuotaStats, 
  QUOTAS_PAR_FORMULE 
} from '../types/quota.types';

// ============================================
// RÉCUPÉRATION DES STATS QUOTA
// ============================================

/**
 * Récupérer les statistiques de quota pour une copropriété
 */
export const getQuotaStats = async (coproprieteId: string): Promise<QuotaStats | null> => {
  try {
    const docRef = doc(db, 'coproprietes', coproprieteId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.error('Copropriété non trouvée:', coproprieteId);
      return null;
    }

    const data = docSnap.data();
    
    // Valeurs par défaut si pas encore configuré
    const formule: FormuleAbonnement = data.formule || 'sentinelle';
    // Fix session 242 : sentinelleUsage est un objet {analysesEffectuees, quota, dateDebut}
    // Ne jamais utiliser directement comme scalaire
    const sentinelleUsageRaw = data.sentinelleUsage;
    const usage = (typeof sentinelleUsageRaw === 'object' && sentinelleUsageRaw !== null)
      ? (sentinelleUsageRaw.analysesEffectuees ?? 0)
      : (typeof sentinelleUsageRaw === 'number' ? sentinelleUsageRaw : 0);
    const quotaFromUsage = (typeof sentinelleUsageRaw === 'object' && sentinelleUsageRaw !== null)
      ? sentinelleUsageRaw.quota
      : undefined;
    const quota = data.sentinelleQuota ?? quotaFromUsage ?? QUOTAS_PAR_FORMULE[formule];
    const resetDate = data.sentinelleResetDate?.toDate() || null;

    const restant = Math.max(0, quota - usage);
    const pourcentage = quota > 0 ? Math.round((usage / quota) * 100) : 0;

    return {
      quota,
      usage,
      restant,
      pourcentage,
      formule,
      resetDate,
      isAlerte80: pourcentage >= 80,
      isAlerte100: pourcentage >= 100,
      isBloque: pourcentage >= 100,
    };
  } catch (error) {
    console.error('Erreur récupération quota:', error);
    return null;
  }
};

// ============================================
// INITIALISATION QUOTA (pour nouvelles copros)
// ============================================

/**
 * Initialiser les champs quota pour une copropriété
 * À appeler lors de la création ou mise à jour d'abonnement
 */
export const initialiserQuota = async (
  coproprieteId: string,
  formule: FormuleAbonnement
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'coproprietes', coproprieteId);
    
    // Date de reset = dans 1 an
    const resetDate = new Date();
    resetDate.setFullYear(resetDate.getFullYear() + 1);

    await updateDoc(docRef, {
      formule,
      sentinelleQuota: QUOTAS_PAR_FORMULE[formule],
      sentinelleUsage: 0,
      sentinelleResetDate: Timestamp.fromDate(resetDate),
      sentinelleAlerte80Envoyee: false,
      sentinelleAlerte100Envoyee: false,
      updatedAt: Timestamp.now(),
    });

    return true;
  } catch (error) {
    console.error('Erreur initialisation quota:', error);
    return false;
  }
};

// ============================================
// AJOUT PACK SUPPLÉMENTAIRE
// ============================================

/**
 * Ajouter un pack de 100 analyses supplémentaires
 */
export const ajouterPackAnalyses = async (
  coproprieteId: string,
  nombrePacks: number = 1
): Promise<boolean> => {
  try {
    const docRef = doc(db, 'coproprietes', coproprieteId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.error('Copropriété non trouvée:', coproprieteId);
      return false;
    }

    const data = docSnap.data();
    const quotaActuel = data.sentinelleQuota ?? 100;
    const analysesAAjouter = nombrePacks * 100;

    await updateDoc(docRef, {
      sentinelleQuota: quotaActuel + analysesAAjouter,
      // Réinitialiser l'alerte 100% si on était bloqué
      sentinelleAlerte100Envoyee: false,
      updatedAt: Timestamp.now(),
    });

    return true;
  } catch (error) {
    console.error('Erreur ajout pack:', error);
    return false;
  }
};

// ============================================
// VÉRIFICATION AVANT ANALYSE
// ============================================

/**
 * Vérifier si une analyse est possible (quota non dépassé)
 * Retourne true si OK, false si bloqué
 */
export const peutFaireAnalyse = async (coproprieteId: string): Promise<boolean> => {
  const stats = await getQuotaStats(coproprieteId);
  
  if (!stats) {
    // En cas d'erreur, on autorise par défaut (fail-open)
    console.warn('Impossible de vérifier le quota, analyse autorisée par défaut');
    return true;
  }

  return !stats.isBloque;
};

// ============================================
// UTILITAIRES D'AFFICHAGE
// ============================================

/**
 * Obtenir la couleur de la barre de progression selon le pourcentage
 */
export const getCouleurQuota = (pourcentage: number): string => {
  if (pourcentage >= 100) return 'bg-red-500';
  if (pourcentage >= 80) return 'bg-amber-500';
  if (pourcentage >= 50) return 'bg-yellow-400';
  return 'bg-green-500';
};

/**
 * Obtenir la couleur du texte selon le pourcentage
 */
export const getCouleurTexteQuota = (pourcentage: number): string => {
  if (pourcentage >= 100) return 'text-red-600';
  if (pourcentage >= 80) return 'text-amber-600';
  return 'text-gray-600';
};

/**
 * Formater le nom de la formule pour affichage
 */
export const getLibelleFormule = (formule: FormuleAbonnement): string => {
  const libelles: Record<FormuleAbonnement, string> = {
    sentinelle: 'SENTINELLE',
    bouclier: 'BOUCLIER',
    forteresse: 'FORTERESSE',
  };
  return libelles[formule];
};

/**
 * Formater la date de reset
 */
export const formaterDateReset = (date: Date | null): string => {
  if (!date) return 'Non définie';
  
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};
